/**
 * Payments / Receipts pilot UI module
 * ------------------------------------
 * Reusable primitives for the pilot:
 *  - InvoiceSummary
 *  - PaymentMethodSelector  (Paystack primary / Hubtel failover placeholders)
 *  - PaymentPendingScreen / PaymentSuccessScreen / PaymentFailedScreen
 *  - ReceiptDownloadButton  (placeholder — geo-api issues signed URL)
 *  - TransactionHistory
 *  - PaymentRemindersList
 *  - PaymentsCenter         (composed view used in InvestorDashboard)
 *
 * No payment keys are held client-side. All flows go through geo-api.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  CreditCard, CheckCircle2, XCircle, Clock, Download, FileText,
  AlertTriangle, RefreshCw, ShieldCheck, Loader2,
} from 'lucide-react';
import {
  paymentService,
  type PaymentRecord,
  type Invoice,
  type PaymentReminder,
  type PaymentProvider,
} from '@/services/paymentService';
import { Button, Card, CardContent, EmptyState } from '@/components/eds';
import { useAuth } from '@/contexts/AuthContext';
import { PaymentEvidenceForm } from './PaymentEvidenceForm';

// P9D: the geo-api payment gateway integration exists but is NOT enabled for
// this pilot. Online payment INITIATION and processor branding are therefore not
// presented as operational; invoices/history remain read-only. Flip only when
// the gateway is genuinely enabled for the pilot (a backend/config decision).
const PAYMENT_INITIATION_ENABLED = false;

const money = (minor: number, currency: string) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' })
    .format(Number(minor || 0));

/* ───────── Invoice summary ───────── */

export const InvoiceSummary: React.FC<{ invoice: Invoice }> = ({ invoice }) => (
  <Card><CardContent className="p-5">
    <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
      <div>
        <div className="text-xs uppercase tracking-wider text-eds-muted-fg">Invoice</div>
        <div className="text-lg font-semibold text-eds-fg font-mono">{invoice.number}</div>
        {invoice.payerOrganization && (
          <div className="text-sm text-eds-muted-fg">{invoice.payerOrganization}</div>
        )}
      </div>
      <span className={`text-xs font-medium px-2.5 py-1 rounded-eds-pill border ${
        invoice.status === 'paid' ? 'bg-eds-success/12 text-eds-emerald-2 border-eds-success/30' :
        invoice.status === 'overdue' ? 'bg-eds-danger/10 text-eds-danger border-eds-danger/30' :
        'bg-eds-warning/15 text-eds-earth border-eds-warning/30'
      }`}>{invoice.status}</span>
    </div>
    <table className="w-full text-sm mb-4">
      <thead className="text-left text-xs uppercase text-eds-muted-fg">
        <tr><th className="py-1">Description</th><th className="text-right">Qty</th><th className="text-right">Amount</th></tr>
      </thead>
      <tbody>
        {invoice.lines.map((l, i) => (
          <tr key={i} className="border-t border-eds-border">
            <td className="py-2">{l.description}</td>
            <td className="py-2 text-right font-mono">{l.quantity}</td>
            <td className="py-2 text-right font-mono">{money(l.line_total ?? (l.unit_amount * l.quantity), invoice.currency)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot className="text-sm">
        <tr><td colSpan={2} className="pt-3 text-right text-eds-muted-fg">Subtotal</td><td className="pt-3 text-right font-mono">{money(invoice.subtotal, invoice.currency)}</td></tr>
        <tr><td colSpan={2} className="text-right text-eds-muted-fg">Tax</td><td className="text-right font-mono">{money(invoice.tax, invoice.currency)}</td></tr>
        <tr><td colSpan={2} className="pt-1 text-right font-semibold text-eds-fg">Total</td><td className="pt-1 text-right font-mono font-semibold">{money(invoice.total, invoice.currency)}</td></tr>
      </tfoot>
    </table>
    {invoice.dueAt && (
      <p className="text-xs text-eds-muted-fg">Due {new Date(invoice.dueAt).toLocaleDateString()}</p>
    )}
  </CardContent></Card>
);

/* ───────── Payment method selector ───────── */

export const PaymentMethodSelector: React.FC<{
  value: PaymentProvider;
  onChange: (p: PaymentProvider) => void;
}> = ({ value, onChange }) => {
  const Card: React.FC<{ p: PaymentProvider; title: string; subtitle: string; tag: string }> = ({ p, title, subtitle, tag }) => {
    const active = value === p;
    return (
      <button
        type="button"
        onClick={() => onChange(p)}
        className={`text-left border rounded-eds-md p-4 transition w-full ${
          active ? 'border-eds-success ring-2 ring-eds-success/20 bg-eds-success/12/40' : 'border-eds-border hover:border-eds-border'
        }`}
      >
        <div className="flex items-center justify-between">
          <span className="font-semibold text-eds-fg">{title}</span>
          <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-eds-pill border bg-eds-surface text-eds-muted-fg border-eds-border">{tag}</span>
        </div>
        <p className="text-xs text-eds-muted-fg mt-1">{subtitle}</p>
      </button>
    );
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <Card p="paystack" title="Paystack" subtitle="Cards · Bank · Mobile money" tag="Primary" />
      <Card p="hubtel"   title="Hubtel"   subtitle="Mobile money · USSD · Cards" tag="Failover" />
    </div>
  );
};

/* ───────── State screens ───────── */

export const PaymentPendingScreen: React.FC<{ reference?: string; onCheck?: () => void }> = ({ reference, onCheck }) => (
  <div className="border border-eds-warning/30 bg-eds-warning/15 rounded-eds-md p-5 flex items-start gap-3">
    <Loader2 className="w-5 h-5 text-eds-earth mt-0.5 animate-spin" />
    <div className="flex-1">
      <div className="font-semibold text-eds-earth">Payment pending</div>
      <p className="text-sm text-eds-earth mt-1">
        We are awaiting confirmation of this payment.
        {reference && <> Reference <span className="font-mono">{reference}</span>.</>}
      </p>
      {onCheck && (
        <button onClick={onCheck} className="mt-3 inline-flex items-center gap-2 text-sm text-eds-earth font-medium hover:underline">
          <RefreshCw className="w-4 h-4" /> Check status
        </button>
      )}
    </div>
  </div>
);

export const PaymentSuccessScreen: React.FC<{ record: PaymentRecord; onReceipt?: () => void }> = ({ record, onReceipt }) => (
  <div className="border border-eds-success/30 bg-eds-success/12 rounded-eds-md p-5 flex items-start gap-3">
    <CheckCircle2 className="w-5 h-5 text-eds-emerald-2 mt-0.5" />
    <div className="flex-1">
      <div className="font-semibold text-eds-success">Payment recorded by the payment processor</div>
      <p className="text-sm text-eds-success mt-1">
        {money(record.amount, record.currency)} captured via <span className="capitalize">{record.provider}</span>.
        Reference <span className="font-mono">{record.reference}</span>.
      </p>
      {onReceipt && (
        <button onClick={onReceipt} className="mt-3 inline-flex items-center gap-2 text-sm text-eds-success font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring rounded-eds-sm">
          <Download className="w-4 h-4" /> Download receipt
        </button>
      )}
    </div>
  </div>
);

export const PaymentFailedScreen: React.FC<{ reference?: string; onRetry?: () => void }> = ({ reference, onRetry }) => (
  <div className="border border-eds-danger/30 bg-eds-danger/10 rounded-eds-md p-5 flex items-start gap-3">
    <XCircle className="w-5 h-5 text-eds-danger mt-0.5" />
    <div className="flex-1">
      <div className="font-semibold text-eds-danger">Payment failed</div>
      <p className="text-sm text-eds-danger mt-1">
        The payment could not be completed.
        {reference && <> Reference <span className="font-mono">{reference}</span>.</>}
        {' '}Review the current invoice status before retrying.
      </p>
      {onRetry && (
        <button onClick={onRetry} className="mt-3 inline-flex items-center gap-2 text-sm text-eds-danger font-medium hover:underline">
          <RefreshCw className="w-4 h-4" /> Retry payment
        </button>
      )}
    </div>
  </div>
);

/* ───────── Receipt download placeholder ───────── */

export const ReceiptDownloadButton: React.FC<{ reference: string }> = ({ reference }) => (
  <span
    className="inline-flex items-center gap-2 text-sm text-eds-muted-fg"
    title={`Receipt PDF generation is pending for ${reference}.`}
  >
    <FileText className="w-4 h-4" />
    Receipt PDF pending
  </span>
);

/* ───────── Transaction history ───────── */

export const TransactionHistory: React.FC<{ rows: PaymentRecord[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return <p className="text-sm text-eds-muted-fg">No payment records yet. Payments appear here once verified by GIISDEC Finance.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-eds-muted-fg">
          <tr>
            <th className="py-2">Reference</th>
            <th>Purpose</th>
            <th>Provider</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Date</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.reference} className="border-t border-eds-border">
              <td className="py-2 font-mono">{r.reference}</td>
              <td>{r.purpose || '—'}</td>
              <td className="capitalize">{r.provider}</td>
              <td className="font-mono">{money(r.amount, r.currency)}</td>
              <td>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-eds-pill border ${
                  r.status === 'success' ? 'bg-eds-success/12 text-eds-emerald-2 border-eds-success/30' :
                  r.status === 'failed'  ? 'bg-eds-danger/10 text-eds-danger border-eds-danger/30' :
                                           'bg-eds-warning/15 text-eds-earth border-eds-warning/30'
                }`}>{r.status}</span>
              </td>
              <td>{new Date(r.createdAt).toLocaleDateString()}</td>
              <td>{r.status === 'success' && <ReceiptDownloadButton reference={r.reference} />}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ───────── Reminders ───────── */

export const PaymentRemindersList: React.FC<{ rows: PaymentReminder[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <div className="border border-dashed border-eds-border rounded-eds-md p-4 text-sm text-eds-muted-fg flex items-center gap-2">
        <Clock className="w-4 h-4" /> No upcoming reminders. Renewal & invoice reminders will appear here.
      </div>
    );
  }
  return (
    <ul className="divide-y divide-eds-border border border-eds-border rounded-eds-md">
      {rows.map((r) => (
        <li key={r.id} className="p-3 flex items-center justify-between">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-eds-earth mt-0.5" />
            <div>
              <div className="font-medium text-eds-fg text-sm">{r.title}</div>
              <div className="text-xs text-eds-muted-fg capitalize">{r.kind} · due {new Date(r.dueAt).toLocaleDateString()}</div>
            </div>
          </div>
          <span className="font-mono text-sm text-eds-fg">{money(r.amount, r.currency)}</span>
        </li>
      ))}
    </ul>
  );
};

/* ───────── Composed center ───────── */

type Phase = 'idle' | 'pending' | 'success' | 'failed';

export const PaymentsCenter: React.FC = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoaded, setInvoicesLoaded] = useState(false);
  const [history, setHistory] = useState<PaymentRecord[]>([]);
  const [reminders, setReminders] = useState<PaymentReminder[]>([]);
  const [provider, setProvider] = useState<PaymentProvider>('paystack');
  const [phase, setPhase] = useState<Phase>('idle');
  const [activePaymentId, setActivePaymentId] = useState<string | undefined>();
  const [activeReference, setActiveReference] = useState<string | undefined>();
  const [lastRecord, setLastRecord] = useState<PaymentRecord | null>(null);
  const [receiptError, setReceiptError] = useState<string | null>(null);

  useEffect(() => {
    paymentService.listInvoices().then((r) => { setInvoices(r.data ?? []); setInvoicesLoaded(true); });
    paymentService.history().then((r) => setHistory(r.data ?? []));
    paymentService.listReminders().then((r) => setReminders(r.data ?? []));
  }, []);

  const selectedInvoice = useMemo(
    () =>
      invoices.find(
        (i) =>
          i.status !== 'paid' &&
          i.status !== 'void' &&
          i.status !== 'refunded'
      ),
    [invoices]
  );

  const hasInvoices = invoices.length > 0;
  const hasPaidInvoices = invoices.some((i) => i.status === 'paid');

  const startPayment = async () => {
    if (!selectedInvoice) {
      setPhase('idle');
      return;
    }

    if (!user?.email) {
      setPhase('failed');
      return;
    }
    setPhase('pending');
    const r = await paymentService.initialize({
      amount: selectedInvoice.total,
      currency: (selectedInvoice.currency as 'GHS' | 'NGN' | 'USD') || 'GHS',
      preferredGateway: provider,
      customerEmail: user.email,
      invoiceId: selectedInvoice.id,
    });
    if (r.data?.authorizationUrl && r.data.payment?.id) {
      setActivePaymentId(r.data.payment.id);
      setActiveReference(r.data.payment.reference);
      // In the pilot we stay on the pending screen; geo-api webhook will flip status.
      window.open(r.data.authorizationUrl, '_blank', 'noopener,noreferrer');
    } else {
      setPhase('failed');
    }
  };

  const checkStatus = async () => {
    if (!activePaymentId) return;
    const r = await paymentService.verify(activePaymentId);
    const payment = r.data?.payment;

    if (payment?.status === 'success') {
      setLastRecord(payment);
      setPhase('success');
    } else if (
      payment?.status === 'failed' ||
      payment?.status === 'cancelled'
    ) {
      setPhase('failed');
    }
  };

  // Receipt records remain available through geo-api, but receipt PDF
  // generation/download is not implemented in the current backend contract.
  // Keep the workflow visible and surface a governed deferred state.
  const downloadReceipt = async () => {
    setReceiptError(
      'Receipt PDF generation is not yet available. Your receipt record remains available in the portal.'
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-xs text-eds-muted-fg">
        <ShieldCheck className="w-4 h-4 text-eds-emerald-2" />
        Payments are verified manually by GIISDEC Finance. There is no online checkout in the Investor Portal.
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {selectedInvoice ? (
            <InvoiceSummary invoice={selectedInvoice} />
          ) : hasInvoices ? (
            <>
              <InvoiceSummary invoice={invoices[0]} />
              <div className="border border-eds-emerald-2/30 bg-eds-emerald-2/5 rounded-eds-md p-4 text-sm text-eds-success flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  {hasPaidInvoices
                    ? 'Payment completed. There are no outstanding invoices requiring payment.'
                    : 'There are no invoices currently eligible for payment.'}
                </span>
              </div>
            </>
          ) : (
            <div className="border border-dashed border-eds-border rounded-eds-md p-6 text-sm text-eds-muted-fg flex items-center gap-2">
              <FileText className="w-4 h-4" /> No invoices available.
            </div>
          )}

          {phase === 'pending' && <PaymentPendingScreen reference={activeReference} onCheck={checkStatus} />}
          {phase === 'success' && lastRecord && (
            <>
              <PaymentSuccessScreen record={lastRecord} onReceipt={downloadReceipt} />
              {receiptError && (
                <div role="alert" className="mt-3 text-sm text-eds-danger bg-eds-danger/8 border border-eds-danger/30 rounded-eds-md px-3 py-2">
                  {receiptError}
                </div>
              )}
            </>
          )}
          {phase === 'failed' && (
            <PaymentFailedScreen
              reference={activeReference}
              onRetry={selectedInvoice ? startPayment : undefined}
            />
          )}
        </div>

        <div className="space-y-4">
          {PAYMENT_INITIATION_ENABLED ? (
            <>
              <div>
                <h4 className="text-xs uppercase tracking-wider text-eds-muted-fg mb-2">Payment method</h4>
                <PaymentMethodSelector value={provider} onChange={setProvider} />
              </div>
              {selectedInvoice ? (
                <button
                  onClick={startPayment}
                  disabled={phase === 'pending'}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-eds-success hover:opacity-90 disabled:bg-eds-muted disabled:text-eds-muted-fg disabled:cursor-not-allowed text-white text-sm font-medium rounded-eds-md transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
                >
                  <CreditCard className="w-4 h-4" /> Pay via geo-api
                </button>
              ) : (
                <div className="w-full border border-eds-border bg-eds-muted/40 rounded-eds-md px-4 py-3 text-sm text-eds-muted-fg">
                  {hasPaidInvoices
                    ? 'Invoice paid — no further payment is required.'
                    : 'No invoice is currently payable.'}
                </div>
              )}
            </>
          ) : (
            // P9D: gateway disabled — no active pay CTA, no processor branding.
            // NITA-02A: payment is made outside the Portal; evidence is submitted
            // below (Geo-API RC2) and verified manually by Finance.
            <div className="w-full border border-eds-border bg-eds-muted/40 rounded-eds-md px-4 py-3 text-sm text-eds-muted-fg">
              Online payment is not available. Pay an invoice as instructed by GIISDEC, then submit your payment evidence below. GIISDEC Finance verifies each payment manually before the invoice is marked paid.
            </div>
          )}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-eds-muted-fg mb-2">Upcoming reminders</h4>
            <PaymentRemindersList rows={reminders} />
          </div>
        </div>
      </div>

      <PaymentEvidenceForm invoices={invoices} invoicesLoaded={invoicesLoaded} />

      <div>
        <h4 className="text-xs uppercase tracking-wider text-eds-muted-fg mb-2">Transaction history</h4>
        <TransactionHistory rows={history} />
      </div>
    </div>
  );
};

export default PaymentsCenter;
